/**
 * atcError class.  Handles the display of the errors.  Note: this is a static class,
 * and you should not allocate it.  It doesn't have a "self()" function
 *
 * Usage:
 *    atcError.displayError("My error message.");
 *    atcError.displayWarning("My warning message.");
 *    atcError.displayMessage("My regular message.");
 *
 */
export default class atcError {

    /**
     * Display an error message to the user.
     *
     * @see atcError.displayWarning()
     * @param message
     */
    static displayError(message)
    {
        this._displayMessageArea("Error", message, 'danger');
    }

    /**
     * Display a warning message to the user.
     *
     * @see atcError.displayError()
     * @param message
     */
    static displayWarning(message)
    {
        if (typeof message !== 'string' || message.length === 0) {
            console.warn(new Error().stack);
            message = "There was an unidentified issue -- please refresh. If the issue persists, please contact support";
        }

        this._displayMessageArea("Warning", message, 'warning');
    }

    /**
     * Display a generic message to the user.
     *
     * @see atcError.displayWarning(), atcError.displayError()
     * @param {string} message
     * @param {string=} title
     *
     * @return {jQuery} the dialog that was popped up.
     */
    static displayMessage(message, title)
    {
        title = typeof title === 'string' ? title : "Message";
        return this._displayMessageArea(title, message, 'primary');
    }

    /**
     * Display a message dialog with the option to never show this again.
     *
     * @param {string} message
     * @param {string} title
     * @param {string} itemId
     */
    static displayMessageWithShowOption(message, title, itemId)
    {
        if (! this._getShouldShow(itemId)) {
            let $dlg = this.displayMessage(message, title).on('hidden.bs.modal', () => {
                if ($dlg.find('#NeverShowAgain').is(':checked')) {
                    this._saveNeverShow(itemId);
                }
            });
            this._addNoShowCheckbox($dlg);
        }
    }

    /**
     * Display a popup "success" message with the "never show this again" option.
     *
     * @param {string} title
     * @param {string} message
     * @param {string} itemId
     */
    static displayPopupWithShowOption(title, message, itemId)
    {
        if (! this._getShouldShow(itemId)) {
            let $dlg = this.displayPopup(title, message).on('hidden.bs.modal', () => {
                if ($dlg.find('#NeverShowAgain').is(':checked')) {
                    this._saveNeverShow(itemId);
                }
            });
            this._addNoShowCheckbox($dlg);
        }
    }

    /**
     * Adds the "never show this again" checkbox to a dialog box by looking for the modal-footer class and adding
     * it there.
     *
     * @param $dlg
     * @private
     */
    static _addNoShowCheckbox($dlg)
    {
        const checkbox =
            '<div class="form-group form-check">' +
            '<input type="checkbox" class="form-check-input" id="NeverShowAgain" value="1">' +
            '<label class="form-check-label" for="NeverShowAgain">Never show this again.</label>' +
            '</div>';

        $dlg.find('.modal-footer').prepend(checkbox);
    }

    /**
     * Display a modal popup dialog
     *
     * @param {string} title
     * @param {string} message
     * @returns {jQuery}
     */
    static displayPopup(title, message)
    {
        let $dlg = $('#atc-ErrorDialog');
        $('.modal-title', $dlg).html(title);
        $('.modal-body', $dlg).html(message);
        $dlg.modal('show').on('hidden.bs.modal', () => {
            setTimeout(() => {
                $dlg.modal('dispose').remove();
            });
        });

        return $dlg;
    }

    /**
     * Display a confirmation dialog box.
     *
     * @param {string} title
     * @param {string} message
     * @param {function=} fn
     * @param {string=} itemId
     */
    static displayConfirmation(title, message, fn, itemId)
    {
        let gotOkay = false; // did we get the okay on the confirmation?
        let $dialog = atcError._createPopupDialog(title, message, typeof itemId !== 'undefined').modal('show').on('hidden.bs.modal', () => {
            $dialog.modal('dispose').remove();

            if (typeof fn === 'function') {
                fn(gotOkay);
            }
        });

        $dialog.find(".modal-header").addClass("alert-warning");

        // look for the "okay" button to determine if the user was okay with this action
        $dialog.find('.btn-primary').on('click', () => {
            gotOkay = true;

            if ($dialog.find('#NeverShowAgain').is(':checked')) {
                this._saveNeverShow(itemId);
            }

            $dialog.modal('hide');
        });

    }

    /**
     * Save the state of a "never show this again"
     *
     * @param {string} itemId
     * @private
     */
    static _saveNeverShow(itemId)
    {
        let warnings = localStorage.getItem("warnings");
        if (warnings === null) {
            warnings = "{}";
        }

        warnings = JSON.parse(warnings);
        warnings[itemId] = true;

        localStorage.setItem("warnings", JSON.stringify(warnings));
    }

    /**
     * Display a confirmation popup with the option to never show this again.
     *
     * @param {string} title
     * @param {string} message
     * @param {string} itemId
     * @param {function=} fn
     */
    static displayConfirmationWithShowOption(title, message, itemId, fn)
    {
        if (this._getShouldShow(itemId)) {
            fn(true);
        } else {
            this.displayConfirmation(title, message, fn, itemId);
        }
    }

    /**
     * Checks the local storage if we should "never show this again" option.
     *
     * @param {string} itemId
     * @returns {boolean}
     * @private
     */
    static _getShouldShow(itemId)
    {
        let warnings = localStorage.getItem("warnings");
        if (warnings === null) {
            warnings = "{}";
        }

        warnings = JSON.parse(warnings);
        return typeof warnings[itemId] !== 'undefined' && warnings[itemId] === true;
    }

    /**
     *
     * @param {string} title
     * @param {string} message
     * @param {function} fn
     * @return {jQuery} the dialog we're showing
     */
    static displayModalWithInput(title, message, fn)
    {
        let inputValue = null;
        let isConfirmed = false;

        let $dialog = atcError._createPopupDialog(title, message).modal('show').on('hidden.bs.modal', function () {
            $dialog.modal('dispose');
            if (isConfirmed && typeof fn === 'function') {
                fn(inputValue);
            }
        });

        let body = $dialog.find(".modal-body").html();
        let input = $('<input>').attr('type', 'text').attr('name', 'errorClassInput').addClass("form-control").css("width", "calc(100% - 1.5rem)")[0].outerHTML;
        $dialog.find(".modal-body").html(
            body + "<div class=\"form-group\">" + input + "</div>"
        );

        // look for the "okay" button to determine if the user was okay with this action
        $dialog.find('.btn-primary').on('click', function () {
            isConfirmed= true;
            inputValue = $dialog.find('input[name="errorClassInput"]').val();
            $dialog.modal('hide');
        });

        return $dialog;
    }

    /**
     * Create the jQuery element for the confirmation and popup dialog boxes.
     *
     * @param {String} title
     * @param {String} message
     * @param {boolean=} [warningIcon=false] - display either a check or warning icon. Set this to true to display the warning one.
     * @return {*|jQuery|HTMLElement}
     * @private
     */
    static _createPopupDialog(title, message, warningIcon)
    {
        let $dlg = $('div.room-popup');

        if ($dlg.length !== 0) {
            // Found the dialog, only update the information https://trello.com/c/TMows8dD
            $dlg.find('.modal-header').removeClass('alert-danger').removeClass('alert-warning');
            $dlg.find('.modal-body').html('<p>' + message + '</p>');
            $dlg.find('.modal-title').html(title);
        } else {
            // The dialog was not found, create a new one.
            $dlg = $('<div class="modal room-popup" tabindex="-1" role="dialog">' +
                '<div class="modal-dialog modal-dialog-centered" role="document">' +
                '<div class="modal-content">' +
                '<div class="modal-header">' + '<h5 class="modal-title">' + title + '</h5>' +
                '<button type="button" class="close" data-dismiss="modal" aria-label="Close">' +
                '<span aria-hidden="true">&times;</span>' +
                '</button>' +
                '</div>' +
                '<div class="modal-body">' + '<p>' + message + '</p>' + '</div>' +
                '<div class="modal-footer">' +
                '<button type="button" class="btn btn-secondary" data-dismiss="modal">Cancel</button>' +
                '<button type="button" class="btn btn-primary">Okay</button>' +
                '</div>' +
                '</div>' +
                '</div>' +
                '</div>'
            );
        }

        if (warningIcon) {
            this._addNoShowCheckbox($dlg);
        }

        return $dlg;
    }

    /**
     * Pass your json return object from the server and this will determine if you have an error or not.
     * If it is an error, it will display the error and return false, true on "all is good"
     *
     * @param {object} json
     * @return {boolean} - true for "no error", false for "has error"
     */
    static processPossibleJsonError(json) {
        if (typeof json === 'object' && (json.success === 'error' || json.success === false)) {
            if (typeof json.message === 'string') {
                atcError.displayError(json.message);
            } else if (typeof json.error.message === 'string') {
                atcError.displayError(json.error.message);
            } else {
                if (window.console) {
                    console.error(json);
                }
                atcError.dispayError("Unknown error from the server.");
            }

            return false;
        } else if (typeof json !== 'undefined' && (json.success === 'no' || json.success === 'warning')) {
            atcError.displayWarning(json.message);

            return false;
        }

        return true;
    }

    /**
     * When an ajax error gets called with a "fail", this function will parse the error and give a better error
     *
     * @param msg
     * @param textStatus
     * @param evt
     */
    static displayAJAXError(msg, textStatus, evt) {
        // window.console && console.log(evt);

        // NOTE: you can also switch evt.status which gives the numeric codes : eg: 200, 404, 405, etc.
        switch (textStatus) {
            case 'notmodified':
                msg += ' The server reported the file is not modified and it sould not report this.  Please notify your friendly administrator.';
                break;
            case 'error':
                msg += ' The server encountered an error.  Please notify the administrator.';
                break;
            case 'parsererror':
                msg += ' We could not parse the returned text "' + evt.responseText + '".  Please notify the administrator.';
                break;
            case 'timeout':
                msg += ' The operation timed out.  Please check your internet connection and try again.';
                break;
            case 'success':
            case 'abort':
                break;
        }

        atcError.displayError(msg);
    }

    /**
     * The "message area" is the top part of the screen.  This will display it with the message and errorClass provided.
     *
     * @param {string} title
     * @param {string} message
     * @param {string} errorClass
     * @return {jQuery}
     *
     * @private
     */
    static _displayMessageArea(title, message, errorClass)
    {
        let $dialog = atcError._createPopupDialog(title, message);

        $dialog.find(".modal-header").addClass('alert-' + errorClass);
        $dialog.find(".btn-primary").remove();
        $dialog.find('.modal-footer button').html("Okay");
        $dialog.modal('show').on('hidden.bs.modal', function () {
            // allow other things to work with this before we destroy it.
            setTimeout(() => {
                $dialog.modal('dispose').remove();
            }, 1);
        });

        return $dialog;
    }

    /**
     * Static private method to get a jQuery object representing the container, or allocate it if it doesn't exist.
     *
     * @return {jQuery}
     * @private
     */
    static _getErrorContainer()
    {
        var $container = $('#atcErrorBlock');

        if ($container.length === 0) {
            $container = $('<div/>').attr('id', 'atcErrorBlock').html('<div id="atcMessageArea"></div><input type="button" value="Okay"/>').hide().appendTo($('body'));
            $container.find('input').click(function () {
                $container.slideUp('fast', function () {
                    $container.remove();
                });
            });
        }

        return $container;
    }

    /**
     * Process an error that was provided by the socket communications
     *
     * @param object
     */
    static processSocketError(object) {
        if (window.console) {
            console.error("Got a socket error...");
            console.error(object.message);
        }

        atcError.displayPopup("Socket Error", object.message, '450px');
    }

}
